/*
 * Copyright 2026 Sony Corporation
 */

import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { NodeMaterial } from '@babylonjs/core/Materials/Node/nodeMaterial';
import type { BaseTexture } from '@babylonjs/core/Materials/Textures/baseTexture';
import type { Scene } from '@babylonjs/core/scene';

/**
 * Scene- and process-level state a loading scene is allowed to change.
 *
 * Babylon keeps these outside any object a scene owns, so a loader can leave
 * them poisoned for whatever loads next: `IgnoreTexturesAtLoadTime` is a static,
 * and a stale `environmentTexture` is a reference to a disposed object. They are
 * captured before a scene stages and put back when it is disposed.
 */
export interface SceneGlobals {
  ignoreTexturesAtLoadTime: boolean;
  environmentTexture: BaseTexture | null;
  clearColor: Color4;
  ambientColor: Color3;
  fogMode: number;
  fogColor: Color3;
}

export const snapshotGlobals = (scene: Scene): SceneGlobals => ({
  ignoreTexturesAtLoadTime: NodeMaterial.IgnoreTexturesAtLoadTime,
  environmentTexture: scene.environmentTexture,
  clearColor: scene.clearColor.clone(),
  ambientColor: scene.ambientColor.clone(),
  fogMode: scene.fogMode,
  fogColor: scene.fogColor.clone(),
});

/**
 * The environment texture is cleared first and on purpose: disposing it while
 * the scene still points at it leaves a dangling reference for a frame.
 */
export const restoreGlobals = (scene: Scene, snapshot: SceneGlobals): void => {
  scene.environmentTexture = snapshot.environmentTexture;
  NodeMaterial.IgnoreTexturesAtLoadTime = snapshot.ignoreTexturesAtLoadTime;
  scene.clearColor = snapshot.clearColor.clone();
  scene.ambientColor = snapshot.ambientColor.clone();
  scene.fogMode = snapshot.fogMode;
  scene.fogColor = snapshot.fogColor.clone();
};
